import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	doNothing,
	Else,
	If,
	makeClassName,
	Row,
	StyleProps,
	useTimeout,
} from "@hex-insights/core";
import { Reforge } from "@hex-insights/uploads";
import { Icon } from "../../../../Components/Icon";
import { Image } from "../../../../Components/Image";
import { Video } from "../../../../Components/Video";
import {
	MediaAttachment,
	reforgeSizeMultiple,
	useHorizontalArrowKeyNavigation,
	useSwipeNavigation,
} from "../../../../Utilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import { preloadMediaAttachment } from "./utils";
import styles from "./styles.module.css";

export { getPreloadMediaAttachments } from "./utils";

export const visualAttachmentCarouselLoadingPreviewEdits: Reforge.ImageOptions["edits"] = {
	resize: { width: 75 },
};

export type VisualAttachmentCarouselProps = {
	mediaAttachment: Pick<MediaAttachment, "id" | "fileURL" | "fileKey" | "fileContentType">;
	/**
	 * Use `getPreloadMediaAttachments` to get a value for this prop.
	 */
	preloadMediaAttachments?: Pick<MediaAttachment, "id" | "fileKey" | "fileContentType">[];
	index: number;
	incrementIndex: () => void;
	decrementIndex: () => void;
	totalAttachments: number;
	maxWidth?: number;
} & Partial<ClassNameProps & StyleProps>;

export function VisualAttachmentCarousel({
	mediaAttachment,
	preloadMediaAttachments,
	index,
	incrementIndex,
	decrementIndex,
	totalAttachments,
	maxWidth,
	className,
	style,
}: VisualAttachmentCarouselProps) {
	const hasMultipleAttachments = totalAttachments > 1;

	useHorizontalArrowKeyNavigation(decrementIndex, incrementIndex);
	useSwipeNavigation(decrementIndex, incrementIndex);

	const imageEdits = React.useMemo<Reforge.ImageOptions["edits"] | undefined>(() => {
		if (maxWidth) {
			return { resize: { width: maxWidth * reforgeSizeMultiple } };
		}
		return undefined;
	}, [maxWidth]);

	React.useEffect(() => {
		if (!preloadMediaAttachments) {
			return;
		}
		for (let i = 0; i < preloadMediaAttachments.length; i++) {
			const mediaAttachment = preloadMediaAttachments[i];
			if (!MediaAttachmentUtilities.isImage(mediaAttachment)) {
				continue;
			}
			// preloads full image rather than the loading preview size because it's likely that the user will navigate
			// to this image.
			preloadMediaAttachment(mediaAttachment, imageEdits);
		}
	}, [preloadMediaAttachments, imageEdits]);

	// Blur the image while the loading preivew is shown. The loading preview is blurred by default at first but then
	// blur moves to the main image for the transition (blur must be removed from the loading preview because the blur
	// stacks). This happens after a delay so that the image does not fade in on every render.
	const [isLoadingPreviewActive, setIsLoadingPreviewActive] = React.useState(false);
	const { setTimeout, clearTimeout } = useTimeout();
	React.useEffect(() => {
		setTimeout(() => setIsLoadingPreviewActive(true), 100);
	}, [setTimeout]);
	const onImageLoad = React.useCallback(() => {
		clearTimeout();
		setIsLoadingPreviewActive(false);
	}, [clearTimeout]);

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["visual-attachment-carousel"], className)}
			style={style}
		>
			<Row
				justify="spaced-center"
				align="center"
				className={styles["attachment-container"]}
				style={{ height: "100%", overflow: "hidden" }}
			>
				<If condition={hasMultipleAttachments}>
					<div onClick={decrementIndex} className={styles["nav-button-area"] + " " + styles["nav-button-area--left"]}>
						<div className={styles["nav-button-container"]}>
							<Button variant="link" size="small" onClick={doNothing} className={styles["nav-button"]}>
								<Icon.CaretLeft size={24} className={styles["nav-button__icon--large-screen"] + " block"} />
								<Icon.CaretCircleLeft
									size={24}
									className={styles["nav-button__icon--small-screen"] + " block"}
									weight="fill"
								/>
							</Button>
						</div>
					</div>
				</If>
				<Conditional>
					<If condition={MediaAttachmentUtilities.isVideo(mediaAttachment)}>
						<Video
							key={mediaAttachment.id}
							src={mediaAttachment.fileURL}
							controls
							autoPlay
							loop
							sizing="contain"
							className={styles["video"]}
						/>
					</If>
					<Else>
						<Image
							key={mediaAttachment.id}
							fileKey={mediaAttachment.fileKey}
							contentType={mediaAttachment.fileContentType}
							edits={imageEdits}
							onLoad={onImageLoad}
							sizing="contain"
							className={makeClassName(styles["image"], isLoadingPreviewActive ? styles["image--blurred"] : "")}
							loadingContent={
								<Image
									fileKey={mediaAttachment.fileKey}
									contentType={mediaAttachment.fileContentType}
									edits={visualAttachmentCarouselLoadingPreviewEdits}
									className={makeClassName(
										styles["image__loading-preview"],
										!isLoadingPreviewActive ? styles["image__loading-preview--blurred"] : "",
									)}
								/>
							}
						/>
					</Else>
				</Conditional>
				<If condition={hasMultipleAttachments}>
					<div onClick={incrementIndex} className={styles["nav-button-area"] + " " + styles["nav-button-area--right"]}>
						<div className={styles["nav-button-container"]}>
							<Button variant="link" size="small" onClick={doNothing} className={styles["nav-button"]}>
								<Icon.CaretRight size={24} className={styles["nav-button__icon--large-screen"] + " block"} />
								<Icon.CaretCircleRight
									size={24}
									className={styles["nav-button__icon--small-screen"] + " block"}
									weight="fill"
								/>
							</Button>
						</div>
					</div>
				</If>
			</Row>

			<If condition={hasMultipleAttachments}>
				<div className={styles["attachment-pagination-info"]}>
					{index + 1} of {totalAttachments}
				</div>
			</If>
		</Column>
	);
}
